import { useEffect, useRef } from 'react';
import { useNewsProximity, NewsProximityEvent } from '../hooks/useNewsProximity';
import { useActiveBrokerPositions } from '../store/brokerStore';
import { addToast } from '../store/toastStore';

// Module-level: survives HMR, remounts, strict mode.
// Keyed by content so ID instability from scrape cycles can't cause dupes.
const toastedKeys = new Set<string>();

/**
 * Headless component that emits toast warnings at 10min and 5min
 * before high-importance calendar events.
 *
 * On the first proximity check after MOUNT, everything already inside the
 * window is silently added to the dedup set so it doesn't toast — that is what
 * stops a burst on page load. Only events that newly enter the window toast.
 *
 * A reconnect or a news-service restart does NOT re-seed, despite what the note
 * here used to claim: `news:init` replaces the store without remounting this
 * component. It doesn't need to. `toastedKeys` is module-level and keyed by
 * content, so anything already warned about stays suppressed, and an event that
 * entered the window while the socket was down still deserves its warning.
 */
const NewsWarningEffect: React.FC = () => {
  const { nearbyEvents } = useNewsProximity();
  const positions = useActiveBrokerPositions();
  const firstRun = useRef(true);

  const hasActivePosition = positions.some((p) => p.netPos !== 0);

  useEffect(() => {
    // First run after mount or HMR: seed the dedup set with everything
    // already in the proximity window. No toasts — these aren't "new".
    //
    // This used to also test a module-level `initialized` flag, which could
    // only ever be false on the same pass where `firstRun` was already true.
    const releases = dedupeByRelease(nearbyEvents);

    if (firstRun.current) {
      firstRun.current = false;
      for (const ev of releases) {
        toastedKeys.add(makeKey(ev, 10));
        if (ev.minutesUntil <= 5) {
          toastedKeys.add(makeKey(ev, 5));
        }
      }
      return;
    }

    for (const ev of releases) {
      fireIfNew(ev, 10, hasActivePosition);
      if (ev.minutesUntil <= 5) {
        fireIfNew(ev, 5, hasActivePosition);
      }
    }
  }, [nearbyEvents, hasActivePosition]);

  return null;
};

/**
 * One key per RELEASE, not per event row.
 *
 * Agencies publish several series from a single print: a UK GDP release arrives
 * as "GDP Estimate 3M/3M", "GDP Estimate MoM" and "GDP Estimate YoY", three
 * calendar rows sharing one timestamp. Keying on `ev.event` made those three
 * separate warnings, and with Manufacturing Production alongside them at the
 * same instant that was four toasts per threshold — eight on screen for a
 * single moment you needed to be flat for.
 *
 * Time is the right identity because the warning is about the MOMENT, not the
 * statistic: "be flat at 09:00" is one instruction however many series print.
 * Country stays in the key so two countries releasing on the same minute are
 * still two distinct warnings.
 */
export function makeKey(ev: NewsProximityEvent, threshold: number): string {
  return `${ev.country}|${ev.timestamp}:${threshold}`;
}

/**
 * One representative event per (country, timestamp, threshold).
 *
 * Dedup has to happen BEFORE the toast text is built, not inside `fireIfNew`:
 * the label is derived from `ev.event`, so picking the first row is also
 * picking which name the user reads. Shortest name wins — of "GDP Estimate
 * 3M/3M", "GDP Estimate MoM" and "GDP Estimate YoY" the shortest is the least
 * qualified and so the best stand-in for the release as a whole.
 */
export function dedupeByRelease(events: NewsProximityEvent[]): NewsProximityEvent[] {
  const best = new Map<string, NewsProximityEvent>();
  for (const ev of events) {
    const k = `${ev.country}|${ev.timestamp}`;
    const held = best.get(k);
    if (!held || ev.event.length < held.event.length) best.set(k, ev);
  }
  return [...best.values()].sort((a, b) => a.timestamp - b.timestamp);
}

function fireIfNew(ev: NewsProximityEvent, threshold: number, hasPosition: boolean) {
  const key = makeKey(ev, threshold);
  if (toastedKeys.has(key)) return;
  toastedKeys.add(key);

  const isEarnings = ev.category === 'earnings';
  const prefix = threshold === 5 ? '5 min' : '10 min';
  const icon = isEarnings ? '📊' : threshold === 5 ? '🚨' : '⚠️';
  // For earnings, label with the ticker when known (e.g. "MU") and drop the country.
  const label = isEarnings ? `${ev.ticker ? ev.ticker + ' ' : ''}${ev.event}` : `${ev.event} (${ev.country})`;

  if (hasPosition && threshold === 5) {
    addToast('warning', `${icon} ${label} in ${prefix} — CLOSE YOUR TRADE!`, 12_000);
  } else if (hasPosition) {
    addToast('warning', `${icon} ${label} in ${prefix} — Consider closing your position!`, 12_000);
  } else {
    addToast('info', `${icon} ${label} in ~${ev.minutesUntil} min`, 8_000);
  }
}

export default NewsWarningEffect;
